import { ChangeEvent, useEffect, useState } from "react";

import { Card, FontIcon } from "@ezmid/components-library";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as yup from "yup";

import { useUpdateLecturerByIdMutation } from "../../../api/lecturer.generated";
import { EntityLecturer } from "../../../api/lecturers.generated";
import { Optional } from "../../../enums/types";
import { useUpload, useUploadMedia } from "../../../hooks";
import { useGetImage } from "../../../hooks/useGetImage";
import { emailRegex } from "../../../utils/isEventTriggeredInElement";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { PageLoader } from "../../loader/PageLoader";
import { LecturerDetailForm } from "../molecules/LecturerDetailForm";

type FormValues = {
  fullName: string;
  descriptionEN: string;
  descriptionDE: string;
  email: string;
};
type Props = {
  currentData?: EntityLecturer;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  id?: string;
};
export function LecturerDetailPageContent({
  currentData,
  isFetching,
  id,
  isError,
  isSuccess,
}: Props) {
  const { t } = useTranslation();
  const [lecturerUpdate] = useUpdateLecturerByIdMutation();
  const { addFileToFormData } = useUpload();
  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);

  const FormSchema = yup.object().shape({
    fullName: yup.string().required("Full name is required"),
    email: yup
      .string()
      .matches(emailRegex, t("form.email.invalid"))
      .required(t("form.email.required")),
    descriptionEN: yup.string().required(t("form.description.required")),
  });

  const enDescription = currentData?.translations?.find(
    ({ language }) => language === "en"
  )?.description;
  const deDescription = currentData?.translations?.find(
    ({ language }) => language === "de"
  )?.description;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      fullName: currentData?.fullName,
      descriptionEN: enDescription,
      descriptionDE: deDescription,
      email: currentData?.contactEmail,
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const [lecturerImageId, setLecturerImageId] = useState<string>(
    currentData?.lecturerImage?.id || ""
  );
  const [imageFormData, setImageFormData] = useState<Optional<FormData>>(null);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(imageFormData);
  const { downloadedImage, isImageDownloading } = useGetImage(
    uploadedMediaId || lecturerImageId
  );

  useEffect(
    function resetFormDataAfterFetch() {
      if (!isFetching && !!currentData) {
        reset({
          fullName: currentData?.fullName,
          descriptionEN: enDescription,
          descriptionDE: deDescription,
          email: currentData.contactEmail,
        });
      }
    },
    [currentData]
  );

  useEffect(
    function getLecturerImageAfterUserDataFetch() {
      if (!isFetching && !isError && isSuccess) {
        currentData?.lecturerImage?.id &&
          setLecturerImageId(currentData?.lecturerImage?.id);
        setIsDirtyFromImage(false);
      }
    },
    [currentData, isFetching, isError, isSuccess]
  );

  const onSubmit = () => {
    const { fullName, descriptionDE, descriptionEN, email } = getValues();
    if (currentData?.translations?.length) {
      const updatedTranslations = currentData?.translations?.map((item) => ({
        ...item,
        description: item?.language === "en" ? descriptionEN : descriptionDE,
        language: item?.language === "en" ? "en" : "de",
      }));

      setIsDirtyFromImage(false);
      lecturerUpdate({
        id: currentData?.id || "",
        lecturerUpdateRequest: {
          fullName,
          contactEmail: email,
          translations: updatedTranslations,
          lecturerImageID: uploadedMediaId || currentData?.lecturerImage?.id,
        },
      })
        .unwrap()
        .then(() => {
          reset({
            fullName,
            descriptionEN,
            descriptionDE,
          });
          toast.success(t("app.lecturer.edit.success"));
        })
        .catch((error) => {
          const { hasFieldErrors, errorMessage } = handleValidationErrors(
            error,
            setError
          );
          if (!hasFieldErrors) {
            toast.warning(errorMessage || t("app.lecturer.edit.failed"));
          }
        });
    }
  };

  const onChangePicture = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setImageFormData(addFileToFormData(e.target.files[0]));
      setIsDirtyFromImage(true);
    }
  };

  const isFormDirty = isDirty || isDirtyFromImage;
  const isImageLoading = isImageDownloading || isMediaUploading;
  const isSubmitButtonDisabled = !isFormDirty || isImageLoading || isFetching;

  return isFetching ? (
    <PageLoader />
  ) : (
    <div className="w-full min-w-full flex justify-center">
      <div className="w-1/2 h-[calc(100%_-_182px)] overflow-hidden">
        <Card
          size="fill"
          shadows="xl"
          headerText={t("user.personal.information")}
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          }
        >
          <LecturerDetailForm
            control={control}
            errors={errors}
            onChangePicture={onChangePicture}
            handleSubmit={handleSubmit(onSubmit)}
            lecturerImage={downloadedImage || ""}
            isImageLoading={isImageLoading}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            onSumbit={onSubmit}
          />
        </Card>
      </div>
    </div>
  );
}
